import { FC, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

import DataList from 'shared-components/atom/dataList';
import Select from 'shared-components/atom/select';
import Switcher from 'shared-components/molecule/switcher';
import CustomMultiSelect from 'shared-components/molecule/multiselect';

import { socialOptions } from '../../constants';
import { useAutomationGlobalMethods } from '../../hooks';
import { AutomationSettingsFactoryTypesProps } from '../../types';
import { proxyList } from 'constants/index';
import { residential_geo_proxy_country_list } from 'constants/countries/residential-geo-proxy-countries';
import { cacheFunction } from 'helpers';

import { infoIcon } from 'assets/svg-icons';
import './styles.scss';

const resultsLimitOptions = [
  { label: 'All', value: 'all' },
  { label: '1', value: 1 },
  { label: '5', value: 5 },
  { label: '10', value: 10 },
];

const SocialLinkScraper: FC<AutomationSettingsFactoryTypesProps> = ({
  automationSendSettings,
  automationIndex,
  selectedRecipes,
  setAutomationSendSettings,
  setCompletedFields,
  id,
  isSettingsChange,
  setIsDirty,
  automationOutputTypes,
  automationInputTypes,
}) => {
  const [selected, setSelected] = useState<any>([]);
  const [fieldList, setFieldList] = useState<{ label: string; value: string }[]>([]);
  const [fieldListWithTypes, setFieldListWithTypes] = useState([]);
  const [clicked, setClicked] = useState(false);
  const [selectedSource, setSelectedSource] = useState<any>([]);
  const [selectedProxy, setSelectedProxy] = useState({ label: 'Data Center', value: '' });
  const [residentialGEOCountry, setResidentialGEOCountry] = useState('');
  const [selectedMode, setSelectedMode] = useState('Standard');
  const [resultsLimit, setResultsLimit] = useState<{ label: string; value: number | string }>({
    label: '5',
    value: 5,
  });

  const {
    getDynamicAndIntegrationsSettings,
    getPreviousAutomationOutputOrPreviousRecipKeys,
    getPreviousAutomationOutputOrPreviousRecipKeysWithTypes,
    liftingSettings,
    restoreSelectedSource,
  } = useAutomationGlobalMethods({
    automationSendSettings,
    setAutomationSendSettings,
    selectedRecipes,
    automationIndex,
    id,
  });

  const checkFields = () => {
    if (selected?.length === 0) {
      return false;
    }
    if (selectedProxy?.value === 'premium' && !residentialGEOCountry) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    const { predefined_settings, dynamic_settings } = getDynamicAndIntegrationsSettings();

    if (dynamic_settings && fieldList.length > 0) {
      const key = dynamic_settings.keys[0].key;
      restoreSelectedSource({ key: key, fieldList: fieldList, setSelectedSource: setSelectedSource });
      const { keys } = dynamic_settings;
      const { settings } = keys[0];
      const media = settings ? settings.media : predefined_settings?.media;
      const proxy = settings ? settings.proxy : predefined_settings?.proxy;
      const fast = settings && settings.fast ? 'Fast' : 'Standard';
      setSelectedMode(fast);

      const selectedOptions = media?.map((social: string) => {
        const finded = socialOptions.find(item => item.value === social || item.label === social);
        return finded ? finded : { label: social, value: social };
      });
      selectedOptions && setSelected(selectedOptions);
      
      const maxResults = settings ? settings.maxResults : keys[0].maxResults;

      if (maxResults) {
        const maxResults_selected = resultsLimitOptions.find(item => item.value === maxResults);
        maxResults_selected && setResultsLimit(maxResults_selected);
      }
      
      if (proxy) {
        const selectedProxy = proxyList.find((val: any) => val.value === proxy.type);
        const selectedCountry = residential_geo_proxy_country_list.find(
          (country: any) => country.value === proxy.country_code,
        );
        selectedProxy && setSelectedProxy(selectedProxy);
        selectedCountry && setResidentialGEOCountry(selectedCountry.label);
      }
    }
  }, [fieldList]);

  //recipe fields or output fields
  useEffect(() => {
    // if (fieldList.length > 0) return;
    if (automationIndex === 0) return;
    let options = getPreviousAutomationOutputOrPreviousRecipKeys();
    const optionsWithTypes = getPreviousAutomationOutputOrPreviousRecipKeysWithTypes();
    const filteredOptions =
      automationInputTypes && !automationInputTypes.includes('any')
        ? options.filter(
            (item: any) =>
              (optionsWithTypes &&
                optionsWithTypes[item.value] &&
                (automationInputTypes.includes(optionsWithTypes[item.value]) ||
                  optionsWithTypes[item.value] === 'any')) ||
              !(optionsWithTypes && optionsWithTypes[item.value]),
          )
        : [...options];

    setFieldList(filteredOptions);
    setFieldListWithTypes(optionsWithTypes);
    // setFieldList(options);
  }, [isSettingsChange]);

  useEffect(() => {
    if (fieldList?.length === 1) {
      setSelectedSource(fieldList[0]);
      setIsDirty();
      setClicked(true);
    }
  }, [fieldList]);

  useEffect(() => {
    // if (fieldList.length > 0) return;
    if (automationIndex === 0) return;
    let options = getPreviousAutomationOutputOrPreviousRecipKeys();
    const optionsWithTypes = getPreviousAutomationOutputOrPreviousRecipKeysWithTypes();
    const { dynamic_settings } = getDynamicAndIntegrationsSettings();

    const filteredOptions =
      automationInputTypes && !automationInputTypes.includes('any')
        ? options.filter(
            (item: any) =>
              (optionsWithTypes &&
                optionsWithTypes[item.value] &&
                (automationInputTypes.includes(optionsWithTypes[item.value]) ||
                  optionsWithTypes[item.value] === 'any')) ||
              !(optionsWithTypes && optionsWithTypes[item.value]),
          )
        : [...options];
    let changed = false;
    filteredOptions.map((item: any) => !fieldList.find((sub: any) => sub.value === item.value) && (changed = true));
    const update = filteredOptions.length !== fieldList.length || changed;
    if (filteredOptions.length === 0) {
      setSelectedSource(null);
      setClicked(true);
    }
    if (update) {
      const selOptionFound = filteredOptions.find((item: any) => dynamic_settings?.keys?.[0]?.key === item.value);
      setSelectedSource(!selOptionFound ? null : selOptionFound);
      setClicked(true);
      setFieldList(filteredOptions);
      setFieldListWithTypes(optionsWithTypes);
    }
  }, [automationSendSettings]);

  useEffect(() => {
    if (fieldList?.length) {
      if (clicked) return;
      const jsonParse = automationOutputTypes ? JSON.parse(automationOutputTypes) : {};
      liftingSettings({
        outputKeysData: [
          ...fieldList.map((item: { value: any }) => item.value),
          ...selected.map((social: { value: any }) => social.value),
        ],
        outputKeysDataWithTypes: { ...fieldListWithTypes, ...jsonParse },
      });
    }
  }, [fieldList, fieldListWithTypes, automationOutputTypes, selected]);

  //memoize
  const handleResidentialCountryOptions = (residentialGEOCountry: string) => {
    return residential_geo_proxy_country_list.find(item => item.label === residentialGEOCountry)?.value;
  };
  const getMemoizeResidentialCountryOption = useRef(cacheFunction(handleResidentialCountryOptions));

  //lifting data
  useEffect(() => {
    if (!clicked) return;
    setCompletedFields({ index: automationIndex, checked: checkFields() });
    // setClicked(false);

    let localSettings = {
      media: selected.map((social: { value: any }) => social.value),
      proxy: {
        type: selectedProxy.value,
        country_code: getMemoizeResidentialCountryOption.current(residentialGEOCountry) || '',
      },
      maxResults: resultsLimit.value,
      fast: selectedMode === 'Fast',
    };
    const jsonParse = automationOutputTypes ? JSON.parse(automationOutputTypes) : {};
    liftingSettings({
      dynamicSettings: {
        keys: [
          {
            key: fieldList.length && selectedSource ? selectedSource.value : null,
            inputValue: null,
            settings: localSettings,
          },
        ],
      },
      preDefinedSettings: {
        browser: false,
      },
      outputKeysData: [
        ...fieldList.map((item: { value: any }) => item.value),
        ...selected.map((social: { value: any }) => social.value),
      ],
      outputKeysDataWithTypes: { ...fieldListWithTypes, ...jsonParse },
    });
  }, [
    selected,
    selectedSource,
    selectedProxy,
    residentialGEOCountry,
    fieldList,
    fieldListWithTypes,
    automationOutputTypes,
    selectedMode,
    resultsLimit,
  ]);

  const handleChangeResidentialGEOCountry = (text: string) => {
    // setError('');
    setResidentialGEOCountry(text);
    !clicked && setClicked(true);
    setIsDirty();
  };

  const handleSwitcherClick = (val: string) => {
    setSelectedMode(val);
    !clicked && setClicked(true);
    setIsDirty();
  };

  return (
    <div className="social-link-scraper-settings">
      <Row>
        <Col md={12} className="d-flex">
          <Switcher
            leftText="Fast"
            rightText="Standard"
            handleSwitcherClick={handleSwitcherClick}
            selectedValue={selectedMode}
            hoverTextLeft="Loads pages faster but may have a lower success rate."
            // hoverTextRight="Loads pages slower but may have a higher success rate."
          />
        </Col>
        <Col lg={6} className="mb-4">
          <span className="label secondary-color">Source</span>
          <Select
            fullWidth
            options={fieldList}
            value={Array.isArray(selectedSource) ? selectedSource[0] : selectedSource}
            placeholder={'Select source'}
            onChange={(selectedItem: any) => {
              setSelectedSource(selectedItem);
              !clicked && setClicked(true);
              setIsDirty();
            }}
          />
        </Col>
        <Col lg={4}>
          <span className="label secondary-color">Select social links</span>
          <CustomMultiSelect
            options={socialOptions}
            value={selected}
            onChange={(sel: any) => {
              setSelected(sel);
              !clicked && setClicked(true);
              setIsDirty();
            }}
            labelledBy="Select"
          />
        </Col>
        <Col lg={2} className="mb-2">
          <span className="label secondary-color">Result limit</span>
          <Select
            fullWidth
            options={resultsLimitOptions}
            value={resultsLimit}
            placeholder={'Select limit'}
            onChange={(selectedItem: any) => {
              setResultsLimit(selectedItem);
              !clicked && setClicked(true);
              setIsDirty();
            }}
          />
        </Col>
      </Row>
      <Row className="automation-proxy-container">
        <Col md={6}>
          <span className="label secondary-color">Proxy mode</span>
          <Select
            options={proxyList}
            value={selectedProxy}
            placeholder={'proxy mode'}
            onChange={(selectedItem: any) => {
              setSelectedProxy(selectedItem);
              !clicked && setClicked(true);
              setIsDirty();
            }}
          />
        </Col>
        {selectedProxy.value === 'premium' && (
          <Col md={6}>
            <span className="label secondary-color">Country</span>
            <DataList
              list={residential_geo_proxy_country_list}
              value={residentialGEOCountry}
              placeholder={'Select country'}
              onChange={(text: string) => {
                handleChangeResidentialGEOCountry(text);
              }}
            />
          </Col>
        )}
        {selectedProxy.value && (
          <Col md={12} className="mt-2">
            <span className="info-icon">{infoIcon}</span>
            <span className="info-message">
              This proxy consumes {selectedProxy.value === 'premium' ? '0.12' : '0.1'} premium credits per MB
            </span>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default SocialLinkScraper;
